import { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { Loader2, Lock } from 'lucide-react';
import { App as CapacitorApp } from '@capacitor/app';
import { NativeBiometric } from '@capgo/capacitor-native-biometric';
import { Capacitor } from '@capacitor/core';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Funds from './pages/Funds';
import Settings from './pages/Settings';
import Allocations from './pages/Allocations';
import MoneyMap from './pages/MoneyMap';
import Ledger from './pages/Ledger';
import Auth from './pages/Auth';
import KnowledgeBase from './pages/KnowledgeBase';
import Analytics from './pages/Analytics';
import Subscriptions from './pages/Subscriptions';
import AdminDashboard from './pages/AdminDashboard';
import Landing from './pages/Landing';
import Onboarding from './pages/Onboarding';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import api from './api';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isBlurred, setIsBlurred] = useState(false);
  const [isLocked, setIsLocked] = useState(Capacitor.isNativePlatform() && !!localStorage.getItem('thread_user') && localStorage.getItem('thread_biometrics_enabled') === 'true'); // Lock by default on mobile if logged in & biometrics enabled

  const isAuthenticating = useRef(false);

  useEffect(() => {
    // Biometric & App State setup
    if (Capacitor.isNativePlatform()) {
      const lockApp = async () => {
        // Only trigger biometric verify if the user is logged in & biometrics enabled
        const hasSession = !!localStorage.getItem('thread_user');
        const biometricsEnabled = localStorage.getItem('thread_biometrics_enabled') === 'true';
        if (!hasSession || !biometricsEnabled) {
          setIsLocked(false);
          return;
        }

        if (isAuthenticating.current) return;
        
        try {
          const result = await NativeBiometric.isAvailable();
          if (result.isAvailable) {
             isAuthenticating.current = true;
             await NativeBiometric.verifyIdentity({
               reason: "Authenticate to access Thread Finance",
               title: "Log In",
             });
             setIsLocked(false);
          } else {
             setIsLocked(false); // No biometrics available, unlock
          }
        } catch (error) {
          console.error("Biometric error:", error);
          // If cancelled or failed, stay locked
        } finally {
          // Add a small delay before releasing the flag to ensure the app has fully resumed
          setTimeout(() => {
            isAuthenticating.current = false;
          }, 500);
        }
      };

      CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (isAuthenticating.current) return; // Ignore state changes caused by the biometric prompt itself
        
        const hasSession = !!localStorage.getItem('thread_user');
        const biometricsEnabled = localStorage.getItem('thread_biometrics_enabled') === 'true';
        if (!isActive) {
          if (hasSession && biometricsEnabled) {
            setIsBlurred(true);
            setIsLocked(true); // Lock it when it goes to background
          }
        } else {
          setIsBlurred(false);
          if (hasSession && biometricsEnabled) {
            lockApp(); // Require unlock when returning
          }
        }
      });
      
      // Initial lock check
      const hasSession = !!localStorage.getItem('thread_user');
      const biometricsEnabled = localStorage.getItem('thread_biometrics_enabled') === 'true';
      if (hasSession && biometricsEnabled) {
        lockApp();
      } else {
        setIsLocked(false);
      }
    } else {
      setIsLocked(false);
    }
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await api.get('/auth/me');
        setUser(res.data.user);
        localStorage.setItem('thread_user', JSON.stringify(res.data.user));
      } catch (err) {
        setUser(null);
        localStorage.removeItem('thread_user');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();

    const handleAuthError = () => setUser(null);
    window.addEventListener('thread_auth_error', handleAuthError);
    return () => window.removeEventListener('thread_auth_error', handleAuthError);
  }, []);

  if (loading) {
    return <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Loader2 className="text-muted" style={{ animation: 'spin 2s linear infinite' }} /></div>;
  }

  if (isLocked && user) {
    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', background: '#020617', color: 'white' }}>
        <Lock size={48} className="text-primary mb-4" />
        <h2 className="mb-2">Thread Finance Locked</h2>
        <p className="text-muted mb-6">Biometric authentication is required</p>
        <button className="btn btn-primary" onClick={() => window.location.reload()}>Tap to Unlock</button>
      </div>
    );
  }

  return (
    <div style={{ filter: isBlurred ? 'blur(15px)' : 'none', transition: 'filter 0.2s', height: '100%', width: '100%' }}>
      <BrowserRouter>
      <Toaster 
        position="bottom-right" 
        toastOptions={{
          style: {
            background: 'rgba(2, 6, 23, 0.9)',
            color: '#fff',
            backdropFilter: 'blur(24px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
          },
          success: {
            iconTheme: {
              primary: '#3b82f6',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={user ? <Navigate to="/" /> : <Auth onLogin={setUser} />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/home" element={<Landing />} />

        {/* Root Route (Conditional: Dashboard if logged in, Landing Page if not) */}
        <Route path="/" element={user ? (user.has_completed_onboarding ? <Layout /> : <Navigate to="/onboarding" replace />) : <Landing />}>
          {user && user.has_completed_onboarding && (
            <>
              <Route index element={<Dashboard />} />
              <Route path="funds" element={<Funds />} />
              <Route path="allocations" element={<Allocations />} />
              <Route path="map" element={<MoneyMap />} />
              <Route path="ledger" element={<Ledger />} />
              <Route path="knowledge" element={<KnowledgeBase />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="subscriptions" element={<Subscriptions />} />
              <Route path="admin" element={<AdminDashboard />} />
              <Route path="settings" element={<Settings />} />
            </>
          )}
        </Route>

        {/* Onboarding Route */}
        <Route path="/onboarding" element={user && !user.has_completed_onboarding ? <Onboarding /> : <Navigate to="/" replace />} />
        
        {/* Fallback to root */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </BrowserRouter>
    </div>
  );
}

export default App;
